import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import { ContentCopy, Share, QrCode } from '@mui/icons-material';
import { userService } from '../../services/api';

interface ReferralStats {
  has_referral_code: boolean;
  code: string | null;
  total_referrals: number;
  referral_link: string | null;
}

const ReferralSystem: React.FC = () => {
  const [referralStats, setReferralStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferralStats();
  }, []);

  const fetchReferralStats = async () => {
    try {
      setLoading(true);
      const response = await userService.getCurrentUser();
      
      if (response && response.referral_stats) {
        setReferralStats(response.referral_stats);
      } else {
        setError('No se pudieron obtener las estadísticas de referidos');
      }
    } catch (error) {
      console.error('Error al obtener estadísticas de referidos:', error);
      setError('Error al cargar las estadísticas de referidos');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Error al copiar al portapapeles:', error);
    }
  };

  const shareReferralLink = async () => {
    if (referralStats?.referral_link) {
      try {
        if (navigator.share) {
          await navigator.share({
            title: '¡Únete a Princonser Kids!',
            text: 'Usa mi código de referido para registrarte:',
            url: referralStats.referral_link
          });
        } else {
          // Fallback para navegadores que no soportan Web Share API
          copyToClipboard(referralStats.referral_link);
        }
      } catch (error) {
        console.error('Error al compartir:', error);
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        🎯 Sistema de Referidos
      </Typography>
      
      <Divider sx={{ my: 2 }} />
      
      {referralStats?.has_referral_code ? (
        <Grid container spacing={3}>
          {/* Código de Referido */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Tu Código de Referido
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Chip 
                    label={referralStats.code} 
                    color="primary" 
                    variant="outlined"
                    sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}
                  />
                  <Button
                    size="small"
                    startIcon={<ContentCopy />}
                    onClick={() => copyToClipboard(referralStats.code!)}
                    variant="outlined"
                  >
                    {copied ? '¡Copiado!' : 'Copiar'}
                  </Button>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Comparte este código con amigos y familiares para que se registren
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Link de Referido */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Link de Referido
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <TextField
                    fullWidth
                    value={referralStats.referral_link || ''}
                    variant="outlined"
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                  <Button
                    size="small"
                    startIcon={<Share />}
                    onClick={shareReferralLink}
                    variant="contained"
                    color="primary"
                  >
                    Compartir
                  </Button>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Envía este enlace directamente a tus contactos
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Estadísticas */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  📊 Estadísticas de Referidos
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box textAlign="center">
                      <Typography variant="h4" color="primary">
                        {referralStats.total_referrals}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Usuarios Referidos
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            🚀 Tu código de referido se generará automáticamente
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cuando te registres, se creará automáticamente un código único que podrás compartir
          </Typography>
        </Box>
      )}

      {/* Información del Sistema */}
      <Box mt={3} p={2} bgcolor="info.light" borderRadius={1}>
        <Typography variant="subtitle2" gutterBottom>
          ℹ️ ¿Cómo funciona el Sistema de Referidos?
        </Typography>
                  <Typography variant="body2" component="div">
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>✅ <strong>Código automático:</strong> Se genera automáticamente al registrarte</li>
              <li>🌳 <strong>Árbol de distribución:</strong> Los usuarios referidos se colocan en tu árbol</li>
              <li>⭐ <strong>Estrella guía:</strong> Cada referido genera una estrella guía automáticamente</li>
              <li>💰 <strong>Distribución automática:</strong> Se activa el sistema de distribución de Tfiats</li>
              <li>📊 <strong>Beneficios:</strong> Consulta tu balance en el Sistema de Compensación</li>
            </ul>
          </Typography>
      </Box>
    </Paper>
  );
};

export default ReferralSystem;
